import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import z from 'zod';

import { ClearableSelect } from '@/components/management/formElements/ClearableSelect';
import { CoordinatesFieldGroup } from '@/components/management/formElements/CoordinatesFieldGroup';
import { RmvField } from '@/components/management/formElements/RmvField';
import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { I18N_DE } from '@/i18n/de';
import type {
  CapacityClass,
  FacilityType,
  Organization,
  ParkAndRideFacility,
  ParkingSpaceMarking,
  ParkingSurface,
  TracingCapacity,
  TracingParkingSpace,
  TrafficSign,
  TypeOfManagement,
  UpdateParkAndRideFacilityDto,
  UsageExtent,
} from '@/modules/api/backend';
import { EntranceProhibited } from '@/modules/api/backend';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

interface EditParkAndRideFacilityFormProps {
  initialValues: ParkAndRideFacility;
  organizations: Organization[];
  setRef: (idx: number) => (ref: HTMLDivElement | null) => void;
  tabs: string[];
  onSubmit: (values: UpdateParkAndRideFacilityDto) => Promise<void>;
  disabled?: boolean;
}

export function EditParkAndRideFacilityForm({
  initialValues,
  organizations,
  setRef,
  tabs,
  onSubmit,
  disabled,
}: EditParkAndRideFacilityFormProps) {
  const stationPrefix = initialValues.station?.rmvNumber ? String(initialValues.station.rmvNumber) : '';
  const fullRmvId = initialValues.rmvId ?? '';

  const form = useAppForm({
    defaultValues: {
      name: initialValues.name,
      entranceStreet: initialValues.entranceStreet,
      entranceHouseNumber: initialValues.entranceHouseNumber,
      entranceSecondaryStreet: initialValues.entranceSecondaryStreet,
      entranceSecondaryHouseNumber: initialValues.entranceSecondaryHouseNumber,
      postalCode: initialValues.postalCode,
      city: initialValues.city,
      trafficSign: initialValues.trafficSign,
      trafficSignAdditional: initialValues.trafficSignAdditional,
      facilityType: initialValues.facilityType,
      prOfficial: initialValues.prOfficial,
      numParkingSpaceTotal: initialValues.numParkingSpaceTotal,
      parkingSpaceLastUpdate: initialValues.parkingSpaceLastUpdate,
      tracingParkingSpace: initialValues.tracingParkingSpace,
      tracingCapacity: initialValues.tracingCapacity,
      capacityPercentage: initialValues.capacityPercentage,
      capacityClass: initialValues.capacityClass,
      capacityDate: initialValues.capacityDate,
      numParkingSpaceDisabled: initialValues.numParkingSpaceDisabled,
      numParkingSpaceWomen: initialValues.numParkingSpaceWomen,
      numParkingSpaceMotorcycle: initialValues.numParkingSpaceMotorcycle,
      prUsageComment: initialValues.prUsageComment,
      usageExtent: initialValues.usageExtent,
      parkingSpaceMarking: initialValues.parkingSpaceMarking,
      lighting: initialValues.lighting,
      toStationAccessibility: initialValues.toStationAccessibility,
      typeOfManagement: initialValues.typeOfManagement,
      entranceHeight: initialValues.entranceHeight,
      entranceWidth: initialValues.entranceWidth,
      entranceProhibited: initialValues.entranceProhibited,
      parkingSurface: initialValues.parkingSurface,
      freeToUse: initialValues.freeToUse,
      managementUrl: initialValues.managementUrl,
      isParkingUnlimited: initialValues.isParkingUnlimited,
      parkingDuration: initialValues.parkingDuration,
      isAlwaysOpen: initialValues.isAlwaysOpen,
      openingHours: initialValues.openingHours,
      usable: initialValues.usable,
      prStatusNote: initialValues.prStatusNote,
      publication: initialValues.publication,
      organizationId: initialValues.organizationId,
      geometry:
        initialValues.geometry && 'coordinates' in initialValues.geometry
          ? {
              longitude: initialValues.geometry.coordinates[0],
              latitude: initialValues.geometry.coordinates[1],
            }
          : { longitude: null as unknown as number, latitude: null as unknown as number },

      rmvSuffix: fullRmvId.startsWith(stationPrefix) ? fullRmvId.slice(stationPrefix.length) : '',
    },
    ...formScrollIntoViewOptions,
    onSubmit: async ({ value: { geometry, rmvSuffix, ...rest } }) => {
      const sanitized = Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v ?? undefined]));
      await onSubmit({
        ...sanitized,
        geometry: {
          type: 'Point',
          coordinates: [geometry.longitude, geometry.latitude],
        },
        rmvId: stationPrefix + rmvSuffix.trim(),
      });

      form.reset();
    },
  });

  useNavigationBlocker(form);

  return (
    <form
      id="parkingFacilityForm"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={6}>
        <Stack spacing={2}>
          <Typography ref={setRef(0)} variant="h2">
            {tabs[0]}
          </Typography>

          <form.AppField
            name="name"
            validators={{
              onChange: z.string().nonempty(),
            }}
          >
            {(field) => <field.TextField label="Name" sx={{ marginBottom: 2 }} />}
          </form.AppField>

          {initialValues.stationId ? (
            <RmvField fields={{ suffix: 'rmvSuffix' }} form={form} prefix={stationPrefix} />
          ) : null}

          <form.AppField
            name="organizationId"
            validators={{
              onChange: z.string().nonempty(),
            }}
          >
            {(field) => (
              <field.SelectField
                disabled={disabled}
                label="Organisation"
                sx={{ marginBottom: 1 }}
                options={organizations.map((org) => ({
                  value: org.id,
                  label: org.name,
                }))}
              />
            )}
          </form.AppField>
        </Stack>

        <Stack gap={2}>
          <Typography ref={setRef(1)} variant="h2">
            {tabs[1]}
          </Typography>
          <Stack direction="column">
            <form.Field name="usable">
              {(field) => (
                <FormControlLabel
                  label="Anlage nutzbar"
                  control={
                    <Switch
                      checked={field.state.value ?? false}
                      onBlur={field.handleBlur}
                      onChange={(_event, checked) => field.handleChange(checked)}
                    />
                  }
                />
              )}
            </form.Field>
            <form.Field name="publication">
              {(field) => (
                <FormControlLabel
                  label="Parkraumangebot öffentlich sichtbar"
                  control={
                    <Switch
                      checked={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(_event, checked) => field.handleChange(checked)}
                    />
                  }
                />
              )}
            </form.Field>
          </Stack>

          <form.Field name="prStatusNote">
            {(field) => (
              <TextField
                fullWidth
                multiline
                label="Hinweis"
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                value={field.state.value ?? ''}
              />
            )}
          </form.Field>
        </Stack>

        <Stack gap={2.5}>
          <Typography ref={setRef(2)} variant="h2">
            {tabs[2]}
          </Typography>
          <CoordinatesFieldGroup disabled={false} fields="geometry" form={form} />
          <Grid container spacing={2}>
            <Grid size={6}>
              <form.AppField
                name="entranceStreet"
                validators={{
                  onChange: z.string().nonempty(),
                }}
              >
                {(field) => <field.TextField label="Zufahrtsstraße 1" />}
              </form.AppField>
            </Grid>

            <Grid size={6}>
              <form.Field name="entranceHouseNumber">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Hausnummer"
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    value={field.state.value ?? ''}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={6}>
              <form.Field name="entranceSecondaryStreet">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Zufahrtsstraße 2"
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    value={field.state.value ?? ''}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={6}>
              <form.Field name="entranceSecondaryHouseNumber">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Hausnummer 2"
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    value={field.state.value ?? ''}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={6}>
              <form.AppField
                name="postalCode"
                validators={{
                  onChange: z.string().regex(/^\d{5}$/, 'Gültige Postleitzahl erforderlich'),
                }}
              >
                {(field) => <field.TextField label="Postleitzahl" />}
              </form.AppField>
            </Grid>

            <Grid size={6}>
              <form.AppField
                name="city"
                validators={{
                  onChange: z.string().nonempty(),
                }}
              >
                {(field) => <field.TextField label="Ort" />}
              </form.AppField>
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(3)} variant="h2">
            {tabs[3]}
          </Typography>
          <form.Field name="trafficSign">
            {(field) => (
              <ClearableSelect
                label="Beschilderung der Anlage"
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value as TrafficSign)}
                onClear={() => field.handleChange(null)}
                value={field.state.value ?? ''}
                options={Object.entries(I18N_DE.TrafficSign).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </form.Field>

          <form.Field name="trafficSignAdditional">
            {(field) => (
              <TextField
                fullWidth
                multiline
                label="Beschilderung Zusatztafel"
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                value={field.state.value ?? ''}
              />
            )}
          </form.Field>

          <form.Field name="facilityType">
            {(field) => (
              <ClearableSelect
                fullWidth
                label="Art der Parkgelegenheit"
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value as FacilityType)}
                onClear={() => field.handleChange(null)}
                value={field.state.value ?? ''}
                options={Object.entries(I18N_DE.FacilityType).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </form.Field>

          <form.Field name="prOfficial">
            {(field) => (
              <FormControl component="fieldset">
                <FormLabel component="legend">Offizielle P+R-Anlage</FormLabel>
                <RadioGroup
                  row
                  onBlur={field.handleBlur}
                  onChange={(_e, v) => field.handleChange(v === 'true')}
                  value={field.state.value ? 'true' : 'false'}
                  sx={{
                    pl: 2,
                    '& .MuiFormControlLabel-root': {
                      mr: 4,
                    },
                  }}
                >
                  <FormControlLabel control={<Radio />} label="Ja" value="true" />
                  <FormControlLabel control={<Radio />} label="Nein" value="false" />
                </RadioGroup>
              </FormControl>
            )}
          </form.Field>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(4)} variant="h2">
            {tabs[4]}
          </Typography>

          <form.Field name="numParkingSpaceTotal">
            {(field) => (
              <TextField
                fullWidth
                label="Gesamtstellplätze"
                onBlur={field.handleBlur}
                type="number"
                value={field.state.value ?? ''}
                onChange={(event) =>
                  field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                }
                slotProps={{
                  htmlInput: {
                    min: 0,
                  },
                }}
              />
            )}
          </form.Field>

          <Grid container spacing={2}>
            <Grid size={4}>
              <form.Field name="numParkingSpaceDisabled">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Behindertenstellplätze"
                    onBlur={field.handleBlur}
                    type="number"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                      },
                    }}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={4}>
              <form.Field name="numParkingSpaceWomen">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Frauenstellplätze"
                    onBlur={field.handleBlur}
                    type="number"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                      },
                    }}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={4}>
              <form.Field name="numParkingSpaceMotorcycle">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Motorradstellplätze"
                    onBlur={field.handleBlur}
                    type="number"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                      },
                    }}
                  />
                )}
              </form.Field>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={6}>
              <form.Field name="tracingCapacity">
                {(field) => (
                  <ClearableSelect
                    fullWidth
                    label="Ermittlung Kapazität"
                    onBlur={field.handleBlur}
                    onClear={() => field.handleChange(null)}
                    value={field.state.value ?? ''}
                    onChange={(event) => {
                      field.handleChange(event.target.value as TracingCapacity);
                    }}
                    options={Object.entries(I18N_DE.TracingCapacity).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={6}>
              <form.Field name="tracingParkingSpace">
                {(field) => (
                  <ClearableSelect
                    fullWidth
                    label="Ermittlung Stellplätze"
                    onBlur={field.handleBlur}
                    onClear={() => field.handleChange(null)}
                    value={field.state.value ?? ''}
                    onChange={(event) => {
                      field.handleChange(event.target.value as TracingParkingSpace);
                    }}
                    options={Object.entries(I18N_DE.TracingParkingSpace).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                )}
              </form.Field>
            </Grid>
          </Grid>

          <form.Field name="parkingSpaceLastUpdate">
            {(field) => (
              <TextField
                fullWidth
                label="Letzte Aktualisierung"
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value || null)}
                type="date"
                value={field.state.value ? field.state.value.split('T')[0] : ''}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { max: new Date().toISOString().split('T')[0] },
                }}
              />
            )}
          </form.Field>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(5)} variant="h2">
            {tabs[5]}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <form.Field
                name="capacityPercentage"
                validators={{
                  onChange: ({ value }: { value: number | null | undefined }) => {
                    if (value !== undefined && value !== null) {
                      if (value < 0) return 'Wert darf nicht negativ sein';
                      if (value > 100) return 'Wert darf nicht über 100% liegen';
                    }

                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <TextField
                    fullWidth
                    error={!!field.state.meta.errors.length}
                    helperText={field.state.meta.errors[0]}
                    label="Kapazität (%)"
                    onBlur={field.handleBlur}
                    type="number"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        max: 100,
                      },
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      },
                    }}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={6}>
              <form.Field name="capacityClass">
                {(field) => (
                  <ClearableSelect
                    fullWidth
                    label="Kapazitätsklasse"
                    onBlur={field.handleBlur}
                    onClear={() => field.handleChange(null)}
                    value={field.state.value ?? ''}
                    onChange={(event) => {
                      field.handleChange(event.target.value as CapacityClass);
                    }}
                    options={Object.entries(I18N_DE.CapacityClass).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                )}
              </form.Field>
            </Grid>
          </Grid>
          <form.Field name="capacityDate">
            {(field) => (
              <TextField
                fullWidth
                label="Datum der Auslastung"
                onBlur={field.handleBlur}
                type="date"
                value={field.state.value ? field.state.value.split('T')[0] : ''}
                onChange={(event) => {
                  field.handleChange(event.target.value || null);
                }}
                slotProps={{
                  htmlInput: {
                    max: new Date().toISOString().split('T')[0],
                  },
                  inputLabel: { shrink: true },
                }}
              />
            )}
          </form.Field>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(6)} variant="h2">
            {tabs[6]}
          </Typography>

          <Grid container spacing={2}>
            <Grid size={12}>
              <form.Field name="prUsageComment">
                {(field) => (
                  <TextField
                    fullWidth
                    multiline
                    label="Kommentar"
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    value={field.state.value ?? ''}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={12}>
              <form.Field name="usageExtent">
                {(field) => (
                  <ClearableSelect
                    fullWidth
                    label="Ausmaß der Nicht-P+R-Nutzung"
                    onBlur={field.handleBlur}
                    onClear={() => field.handleChange(null)}
                    value={field.state.value ?? ''}
                    onChange={(event) => {
                      field.handleChange(event.target.value as UsageExtent);
                    }}
                    options={Object.entries(I18N_DE.UsageExtent).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                )}
              </form.Field>
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(7)} variant="h2">
            {tabs[7]}
          </Typography>
          <form.Field name="parkingSpaceMarking">
            {(field) => (
              <ClearableSelect
                fullWidth
                label="Markierung der Stellplätze"
                onBlur={field.handleBlur}
                onClear={() => field.handleChange(null)}
                value={field.state.value ?? ''}
                onChange={(event) => {
                  field.handleChange(event.target.value as ParkingSpaceMarking);
                }}
                options={Object.entries(I18N_DE.ParkingSpaceMarking).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </form.Field>

          <form.Field name="lighting">
            {(field) => (
              <FormControlLabel
                label="Beleuchtung"
                control={
                  <Switch
                    checked={field.state.value ?? false}
                    onBlur={field.handleBlur}
                    onChange={(_event, checked) => field.handleChange(checked)}
                  />
                }
              />
            )}
          </form.Field>

          <form.Field name="toStationAccessibility">
            {(field) => (
              <FormControlLabel
                label="Barrierefreiheit zur Station"
                control={
                  <Switch
                    checked={field.state.value ?? false}
                    onBlur={field.handleBlur}
                    onChange={(_event, checked) => field.handleChange(checked)}
                  />
                }
              />
            )}
          </form.Field>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(8)} variant="h2">
            {tabs[8]}
          </Typography>

          <form.Field name="typeOfManagement">
            {(field) => (
              <ClearableSelect
                fullWidth
                label="Art der Bewirtschaftung"
                onBlur={field.handleBlur}
                onClear={() => field.handleChange(null)}
                value={field.state.value ?? ''}
                onChange={(event) => {
                  field.handleChange(event.target.value as TypeOfManagement);
                }}
                options={Object.entries(I18N_DE.TypeOfManagement).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </form.Field>

          <Grid container alignItems="center" pl={1.25} spacing={2}>
            <Grid size={2.5}>
              <form.Field name="isAlwaysOpen">
                {(field) => (
                  <FormControlLabel
                    label="24 h geöffnet"
                    control={
                      <Switch
                        checked={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(_event, checked) => field.handleChange(checked)}
                      />
                    }
                  />
                )}
              </form.Field>
            </Grid>
            <Grid size={9.5}>
              <form.Subscribe selector={(state) => state.values.isAlwaysOpen}>
                {(isAlwaysOpen) => (
                  <form.AppField
                    name="openingHours"
                    validators={{
                      onChangeListenTo: ['isAlwaysOpen'],
                      onChange: ({ value, fieldApi }) => {
                        if (!fieldApi.form.getFieldValue('isAlwaysOpen')) {
                          return z.string().nonempty().safeParse(value).error?.issues[0]?.message;
                        }

                        return undefined;
                      },
                    }}
                  >
                    {(field) => <field.TextField disabled={isAlwaysOpen ?? true} label="Öffnungszeiten" />}
                  </form.AppField>
                )}
              </form.Subscribe>
            </Grid>

            <Grid size={2.5}>
              <form.Field name="freeToUse">
                {(field) => (
                  <FormControlLabel
                    label="Kostenfrei nutzbar"
                    control={
                      <Switch
                        checked={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(_event, checked) => field.handleChange(checked)}
                      />
                    }
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={9.5}>
              <form.Subscribe selector={(state) => state.values.freeToUse}>
                {(freeToUse) => (
                  <form.AppField
                    name="managementUrl"
                    validators={{
                      onChangeListenTo: ['freeToUse'],
                      onChange: ({ value, fieldApi }) => {
                        if (!fieldApi.form.getFieldValue('freeToUse')) {
                          return z.url().safeParse(value).error?.issues[0]?.message;
                        }

                        return undefined;
                      },
                    }}
                  >
                    {(field) => (
                      <field.TextField disabled={freeToUse ?? true} label="URL zur Bewirtschaftung" type="url" />
                    )}
                  </form.AppField>
                )}
              </form.Subscribe>
            </Grid>

            <Grid size={2.5}>
              <form.Field name="isParkingUnlimited">
                {(field) => (
                  <FormControlLabel
                    label="Parkdauer unbegrenzt"
                    control={
                      <Switch
                        checked={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(_event, checked) => field.handleChange(checked)}
                      />
                    }
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={9.5}>
              <form.Subscribe selector={(state) => state.values.isParkingUnlimited}>
                {(isParkingUnlimited) => (
                  <form.AppField
                    name="parkingDuration"
                    validators={{
                      onChangeListenTo: ['isParkingUnlimited'],
                      onChange: ({ value, fieldApi }) => {
                        if (!fieldApi.form.getFieldValue('isParkingUnlimited')) {
                          return z.string().nonempty().safeParse(value).error?.issues[0]?.message;
                        }

                        return undefined;
                      },
                    }}
                  >
                    {(field) => <field.TextField disabled={isParkingUnlimited ?? true} label="Parkdauer" />}
                  </form.AppField>
                )}
              </form.Subscribe>
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(9)} variant="h2">
            {tabs[9]}
          </Typography>

          <Grid container spacing={2}>
            <Grid size={6}>
              <form.Field name="entranceHeight">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Einfahrtshöhe"
                    onBlur={field.handleBlur}
                    type="number"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(event.target.value === '' ? null : parseFloat(event.target.value))
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        step: 0.01,
                      },
                      input: {
                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                      },
                    }}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid size={6}>
              <form.Field name="entranceWidth">
                {(field) => (
                  <TextField
                    fullWidth
                    label="Einfahrtsbreite"
                    onBlur={field.handleBlur}
                    type="number"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(event.target.value === '' ? null : parseFloat(event.target.value))
                    }
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        step: 0.01,
                      },
                      input: {
                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                      },
                    }}
                  />
                )}
              </form.Field>
            </Grid>
          </Grid>

          <form.Field name="entranceProhibited">
            {(field) => (
              <TextField
                fullWidth
                select
                label="Verbot für bestimmte Fahrzeugtypen"
                onBlur={field.handleBlur}
                value={field.state.value ?? []}
                onChange={(event) => {
                  const v = event.target.value;
                  field.handleChange(typeof v === 'string' ? (v.split(',') as EntranceProhibited[]) : v);
                }}
                slotProps={{
                  select: { multiple: true },
                  input: {
                    endAdornment:
                      field.state.value && field.state.value.length > 0 ? (
                        <InputAdornment position="end" sx={{ marginRight: 2 }}>
                          <IconButton edge="end" onClick={() => field.handleChange([])} size="small">
                            <XIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  },
                }}
              >
                {Object.entries(EntranceProhibited).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </form.Field>
        </Stack>

        <Stack spacing={2}>
          <Typography ref={setRef(10)} variant="h2">
            {tabs[10]}
          </Typography>
          <form.Field name="parkingSurface">
            {(field) => (
              <ClearableSelect
                fullWidth
                label="Oberfläche"
                onBlur={field.handleBlur}
                onClear={() => field.handleChange(null)}
                value={field.state.value ?? ''}
                onChange={(event) => {
                  field.handleChange(event.target.value as ParkingSurface);
                }}
                options={Object.entries(I18N_DE.ParkingSurface).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
    </form>
  );
}
